import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import CurrencyRate from 'components/CurrencyRate';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  ActionCategory,
  ActionType,
  CustomActionType,
} from 'further-types/lp/action';
import { AllocationRule } from 'further-types/lp/allocations';
import {
  IncomeOrExpenseApportionmentMode,
  IncomeOrExpenseState,
  IncomeOrExpenseType,
} from 'further-types/lp/income-and-expenses';
import { FundingSource } from 'further-types/lp/investment';
import { LpAccrualFrequency } from 'further-types/lp/lp-fee-template';
import { CurrencyRateDetails } from 'further-types/lp/transaction';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column, Row } from 'further-ui/components/Layout';
import {
  ActionCategoryLabels,
  FundingSourceLabels,
  IncomeOrExpenseStateLabels,
} from 'further-ui/labels';
import StaticField from 'further-ui/components/StaticField';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import AllocationsManager, {
  Allocation,
} from 'components/new/AllocationsManager';
import TransactionAllocator, {
  Transaction,
} from 'components/TransactionAllocator';
import useCurrencyRate from 'hooks/lp/data/currencies/useCurrencyRate';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import api from 'lib/trpcClient';
import { compact } from 'lodash';
import { FC, useState } from 'react';
import { skipToken } from '@tanstack/react-query';
import ApportionmentSummary from '../shared/ApportionmentSummary';

type Proposal = {
  category: IncomeOrExpenseType;
  counterparty: string | { id?: string; name: string };
  incomeOrExpenseType: string;
  incomeOrExpenseState: IncomeOrExpenseState;
  fundingSource?: FundingSource;
  currencyCode: CurrencyCode;
  amountExVatTcy: number;
  vatRate: number;
  amountIncVatTcy: number;
  description?: string;
  startDate: string;
  apportionmentMode: IncomeOrExpenseApportionmentMode;
  apportionmentBasis?: string;
  endDate?: string;
  allocationRule: string;
  allocations: Array<{
    commitmentId: string;
    allocationAmountTcy: number;
    allocationPercentage: number;
    excludeFromAllocation: boolean;
  }>;
};

export type ProposalWithTransactions = Proposal & {
  transactions?: Array<Transaction>;
  fxRate?: CurrencyRateDetails | null;
};

const getCounterpartyName = (
  counterparty: string | { id?: string; name: string },
) => (typeof counterparty === 'string' ? counterparty : counterparty.name);

type Props = {
  proposal: Proposal;
  vehicleId: string;
  onApprove: (proposal: ProposalWithTransactions) => void;
  onReject: () => void;
  isExecuting: boolean;
};

const formatApportionmentBasis = (basis?: string) => {
  switch (basis) {
    case 'MONTHLY_ADVANCE':
      return 'Monthly';
    case 'QUARTERLY_ADVANCE':
      return 'Quarterly';
    case 'BIANNUALY_ADVANCE':
      return 'Biannual';
    case 'ANNUALLY_ADVANCE':
      return 'Annual';
    default:
      return basis ?? '-';
  }
};

const ProposalCard: FC<Props> = ({
  proposal,
  vehicleId,
  onApprove,
  onReject,
  isExecuting,
}) => {
  const { vehicle } = useLayoutContext();
  const [allocations, setAllocations] = useState<Array<Allocation>>(
    proposal.allocations,
  );
  const [allocationRule, setAllocationRule] = useState<AllocationRule>(
    proposal.allocationRule as AllocationRule,
  );
  const [transactions, setTransactions] = useState<Array<Transaction>>([]);

  const currency = proposal.currencyCode;
  const baseCurrency = vehicle?.baseCurrency ?? CurrencyCode.GBP;
  const startDate = new CalendarDay(proposal.startDate);
  const isMultiple = proposal.apportionmentMode === 'multiple';

  const { isFetchingRate, fxRate, changeRate } = useCurrencyRate({
    vehicleId,
    currency,
    date: startDate,
    defaultCurrencyRate: null,
    onRateChange: () => {},
  });

  // Look up the type options to determine if this is a custom action type
  const [typeOptions] = api.incomeAndExpenses.getTypeOptions.useSuspenseQuery({
    vehicleId,
  });

  const allOptions = [
    ...typeOptions.incomeTypeOptions,
    ...typeOptions.expenseTypeOptions,
  ];
  const selectedTypeOption = allOptions.find(
    (opt) => opt.value === proposal.incomeOrExpenseType,
  );
  const isCustomActionType =
    selectedTypeOption?.customActionTypeId !== undefined;
  const actionType = isCustomActionType
    ? CustomActionType.CustomAction
    : (proposal.incomeOrExpenseType as ActionType);

  // Fetch apportionment periods preview for multiple mode
  const { data: apportionmentPeriodsData } =
    api.incomeAndExpenses.apportionmentPeriods.useQuery(
      isMultiple && proposal.apportionmentBasis && proposal.endDate
        ? {
            vehicleId,
            apportionmentBasis:
              proposal.apportionmentBasis as LpAccrualFrequency,
            apportionmentStartDate: startDate,
            apportionmentEndDate: new CalendarDay(proposal.endDate),
            amountIncVatTcy: proposal.amountIncVatTcy,
            amountExVatTcy: proposal.amountExVatTcy,
            currency,
            allocations,
          }
        : skipToken,
    );

  // Fetch auto-generated GL transactions (only for single mode shown in proposal card)
  const [actionTransactions] =
    api.incomeAndExpenses.getTransactions.useSuspenseQuery({
      vehicleId,
      actionType,
      customActionTypeId: isCustomActionType
        ? selectedTypeOption?.customActionTypeId
        : undefined,
      incomeOrExpenseState: proposal.incomeOrExpenseState,
      category: proposal.category,
      amountIncVatTcy: proposal.amountIncVatTcy,
      amountExVatTcy: proposal.amountExVatTcy,
      currency,
      transactions,
    });

  const { transactionTypeOptions } = useTransactionTypeSelectorOptions();

  const handleChangeTransactions = (updated: Array<Transaction>) => {
    setTransactions(updated);
  };

  const handleApprove = () => {
    onApprove({
      ...proposal,
      allocations,
      allocationRule,
      transactions: compact(actionTransactions ?? []),
      fxRate,
    });
  };

  const categoryLabel =
    proposal.category === IncomeOrExpenseType.Income
      ? ActionCategoryLabels[ActionCategory.Income]
      : ActionCategoryLabels[ActionCategory.Expense];

  return (
    <Card variant="outlined" sx={{ mt: 1, mb: 1 }}>
      <CardContent>
        <Column spacing="md">
          <Typography variant="h5">Proposed {categoryLabel}</Typography>

          <Row spacing="lg">
            <Box sx={{ flex: 1 }}>
              <Column spacing="sm">
                <StaticField label="Category">
                  <Typography variant="body1">{categoryLabel}</Typography>
                </StaticField>
                <StaticField label="Counterparty">
                  <Typography variant="body1">
                    {getCounterpartyName(proposal.counterparty)}
                  </Typography>
                </StaticField>
                <StaticField label="Type">
                  <Typography variant="body1">
                    {proposal.incomeOrExpenseType}
                  </Typography>
                </StaticField>
                <StaticField label="State">
                  <Typography variant="body1">
                    {IncomeOrExpenseStateLabels[proposal.incomeOrExpenseState]}
                  </Typography>
                </StaticField>
              </Column>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Column spacing="sm">
                <StaticField label="Amount (excl. tax)">
                  <Typography variant="body1">
                    {numberToCurrencyString(proposal.amountExVatTcy, {
                      currency,
                    })}
                  </Typography>
                </StaticField>
                <StaticField label="VAT Rate">
                  <Typography variant="body1">{proposal.vatRate}%</Typography>
                </StaticField>
                <StaticField label="Gross Amount (incl. tax)">
                  <Typography variant="h6">
                    {numberToCurrencyString(proposal.amountIncVatTcy, {
                      currency,
                    })}
                  </Typography>
                </StaticField>
                <StaticField label={isMultiple ? 'Start Date' : 'Ledger Date'}>
                  <Typography variant="body1">{proposal.startDate}</Typography>
                </StaticField>
                {isMultiple && (
                  <>
                    <StaticField label="End Date">
                      <Typography variant="body1">
                        {proposal.endDate}
                      </Typography>
                    </StaticField>
                    <StaticField label="Apportionment Basis">
                      <Typography variant="body1">
                        {formatApportionmentBasis(proposal.apportionmentBasis)}
                      </Typography>
                    </StaticField>
                  </>
                )}
                {proposal.category === 'expense' &&
                  proposal.incomeOrExpenseState !== 'accrued' && (
                    <StaticField label="Funding Source">
                      <Typography variant="body1">
                        {proposal.fundingSource
                          ? FundingSourceLabels[proposal.fundingSource]
                          : '-'}
                      </Typography>
                    </StaticField>
                  )}
                {proposal.description && (
                  <StaticField label="Description">
                    <Typography variant="body1">
                      {proposal.description}
                    </Typography>
                  </StaticField>
                )}
              </Column>
            </Box>
          </Row>

          {isMultiple && apportionmentPeriodsData && (
            <>
              <Typography variant="h6" sx={{ mt: 1 }}>
                Apportionment Periods
              </Typography>
              <ApportionmentSummary
                apportionmentPeriods={apportionmentPeriodsData}
                currency={currency}
              />
            </>
          )}

          <Typography variant="h6" sx={{ mt: 2 }}>
            Account Transactions
          </Typography>
          {vehicle && (
            <>
              {currency !== baseCurrency && (
                <CurrencyRate
                  currency={currency}
                  date={startDate}
                  isFetchingRate={isFetchingRate}
                  vehicle={vehicle}
                  fxRate={fxRate}
                  onRateChange={changeRate}
                />
              )}
              <TransactionAllocator
                transactionTypeOptions={transactionTypeOptions}
                transactions={compact(actionTransactions ?? [])}
                onChange={handleChangeTransactions}
                transactionCurrency={currency}
                fxRate={fxRate}
                vehicle={vehicle}
                transactionsInFuture={startDate.isInFuture()}
              />
            </>
          )}

          <Typography variant="h6" sx={{ mt: 2 }}>
            Allocations
          </Typography>
          <AllocationsManager
            vehicleId={vehicleId}
            allocationRule={allocationRule}
            onAllocationRuleChange={setAllocationRule}
            onAllocationsChange={setAllocations}
            allocatedActionLabel={categoryLabel}
            currency={currency}
            baseCurrency={baseCurrency}
            allocations={allocations}
            allocatedAmountTcy={proposal.amountIncVatTcy}
            date={startDate}
          />

          <Row spacing="md" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleApprove}
              disabled={isExecuting}
            >
              {isExecuting ? 'Creating...' : 'Approve & Create'}
            </Button>
            <Button
              variant="outlined"
              onClick={onReject}
              disabled={isExecuting}
            >
              Cancel
            </Button>
          </Row>
        </Column>
      </CardContent>
    </Card>
  );
};

export default ProposalCard;
